import type {
	MultipleQueriesQuery,
	SearchOptions,
} from '@algolia/client-search'

import { AUTOCOMPLETE_CONFIG } from '@/constants/autocomplete'

import type { AutocompleteIndices, AutocompleteQueryDescriptor } from '../types'
import type { AutocompleteRefinementList } from '../types'
import {
	normalizeAutocompleteLanguageCode,
	shouldShowCuratedAutocompleteSections,
} from '../utils'
import { buildFacetFiltersFromRefinements } from '../utils/buildFacetFiltersFromRefinements'
import { getFacetsForLocale } from '../utils/getFacetsForLocale'

type BuildAutocompleteQueriesParams = {
	query: string
	refinementList: AutocompleteRefinementList
	languageCode: string
	indices: AutocompleteIndices
}

type BuiltAutocompleteQueries = {
	requestDescriptors: AutocompleteQueryDescriptor[]
	searchableTitleAttributes: string[]
}

type AutocompleteSearchParams = SearchOptions & {
	readonly facetQuery?: string
}

const defaultSearchParams: SearchOptions = {
	hitsPerPage: 5,
	highlightPreTag: '__aa-highlight__',
	highlightPostTag: '__/aa-highlight__',
}

const getQueryLanguages = (
	languageCode: string
): readonly [string, ...string[]] => {
	const normalizedLanguageCode = normalizeAutocompleteLanguageCode(languageCode)

	return normalizedLanguageCode === 'en'
		? (['en'] as const)
		: ([normalizedLanguageCode, 'en'] as const)
}

const getSearchFields = (
	queryLanguages: readonly string[]
): {
	searchableTitleAttributes: string[]
	searchableDescriptionAttributes: string[]
} => ({
	searchableTitleAttributes: queryLanguages.map(
		(language) => `title_${language}`
	),
	searchableDescriptionAttributes: queryLanguages.map(
		(language) => `description_${language}`
	),
})

const buildContentQuery = ({
	indexName,
	query,
	queryLanguages,
	searchableTitleAttributes,
	searchableDescriptionAttributes,
	hitsPerPage,
}: {
	indexName: string
	query: string
	queryLanguages: readonly string[]
	searchableTitleAttributes: string[]
	searchableDescriptionAttributes: string[]
	hitsPerPage: number
}): MultipleQueriesQuery => ({
	indexName,
	query,
	params: {
		...defaultSearchParams,
		queryLanguages: [...queryLanguages],
		restrictSearchableAttributes: [
			...searchableTitleAttributes,
			...searchableDescriptionAttributes,
		],
		ignorePlurals: true,
		removeStopWords: true,
		minWordSizefor1Typo: AUTOCOMPLETE_CONFIG.MIN_WORD_SIZE_FOR_1_TYPO,
		disableTypoToleranceOnAttributes: searchableDescriptionAttributes,
		hitsPerPage,
	},
})

const buildSuggestionsQuery = (query: string): MultipleQueriesQuery => ({
	indexName: '',
	query,
	params: {
		...defaultSearchParams,
		hitsPerPage: AUTOCOMPLETE_CONFIG.HITS_PER_PAGE.SUGGESTIONS_AND_PROMPT,
	},
})

const buildFacetQuery = ({
	indexName,
	query,
	facetName,
	facetFilters,
}: {
	indexName: string
	query: string
	facetName: string
	facetFilters: string[]
}): MultipleQueriesQuery => ({
	indexName,
	type: 'facet',
	facet: facetName,
	query,
	params: {
		...defaultSearchParams,
		facetQuery: query,
		maxFacetHits: AUTOCOMPLETE_CONFIG.HITS_PER_PAGE.FACETS,
		facetFilters,
		minWordSizefor1Typo: AUTOCOMPLETE_CONFIG.MIN_WORD_SIZE_FOR_1_TYPO,
	} as AutocompleteSearchParams,
})

export const buildAutocompleteQueries = ({
	query,
	refinementList,
	languageCode,
	indices,
}: BuildAutocompleteQueriesParams): BuiltAutocompleteQueries => {
	const normalizedLanguageCode = normalizeAutocompleteLanguageCode(languageCode)
	const queryLanguages = getQueryLanguages(normalizedLanguageCode)
	const { searchableTitleAttributes, searchableDescriptionAttributes } =
		getSearchFields(queryLanguages)
	const requestDescriptors: AutocompleteQueryDescriptor[] = [
		{
			section: { kind: 'suggestions' },
			request: {
				...buildSuggestionsQuery(query),
				indexName: indices.suggestions,
			},
		},
	]

	if (!shouldShowCuratedAutocompleteSections(query)) {
		return { requestDescriptors, searchableTitleAttributes }
	}

	const facetFilters = buildFacetFiltersFromRefinements(refinementList)

	for (const facetName of getFacetsForLocale(normalizedLanguageCode)) {
		requestDescriptors.push({
			section: { kind: 'facet', facetName },
			request: buildFacetQuery({
				indexName: indices.main,
				query,
				facetName,
				facetFilters,
			}),
		})
	}

	requestDescriptors.push({
		section: { kind: 'playlists' },
		request: buildContentQuery({
			indexName: indices.playlists,
			query,
			queryLanguages,
			searchableTitleAttributes,
			searchableDescriptionAttributes,
			hitsPerPage: AUTOCOMPLETE_CONFIG.HITS_PER_PAGE.PLAYLISTS,
		}),
	})

	requestDescriptors.push({
		section: { kind: 'collections' },
		request: buildContentQuery({
			indexName: indices.collections,
			query,
			queryLanguages,
			searchableTitleAttributes,
			searchableDescriptionAttributes,
			hitsPerPage: AUTOCOMPLETE_CONFIG.HITS_PER_PAGE.COLLECTIONS,
		}),
	})

	return { requestDescriptors, searchableTitleAttributes }
}
